import {useState, useCallback, useEffect} from 'react';
import {message} from "antd";
import IntlMessage from "../../../../../../components/IntlMessage/IntlMessage";
import MicroQualificationService from "../../../../../../services/MicroQualificationService";
import {LocalName} from "../../../../../../utils/LocalName";

export const useCascadingSelects = (form, data) => {
    const [educationFields, setEducationFields] = useState([]);
    const [trainDirections, setTrainDirections] = useState([]);
    const [filteredTrainDirections, setFilteredTrainDirections] = useState([]);
    const [eduProgramGroups, setEduProgramGroups] = useState([]);
    const [filteredEduProgramGroups, setFilteredEduProgramGroups] = useState([]);
    const [partnerOrganizations, setPartnerOrganizations] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(false);

    const getEducationFields = useCallback(async () => {
        setIsLoadingData(true);
        try {
            const {data} = await MicroQualificationService.getEducationFields(1, 100);
            setEducationFields(data.map(field => ({
                value: field.id,
                label: LocalName.getName(field) + " (" + field.code + ")"
            })));
        } catch (error) {
            message.error(<IntlMessage id={'error'}/>);
        } finally {
            setIsLoadingData(false);
        }
    }, []);

    const getTrainingDirections = useCallback(async (id) => {
        setIsLoadingData(true);
        try {
            const {data} = await MicroQualificationService.getTrainingDirections(1, 100, id);
            setTrainDirections(data.map(field => ({
                value: field.id,
                label: LocalName.getName(field) + " (" + field.code + ")",
                fieldId: field.educationField.id
            })));
        } catch (error) {
            message.error(<IntlMessage id={'error'}/>);
        } finally {
            setIsLoadingData(false);
        }
    }, []);

    const getEducationProgramsGroup = useCallback(async (id) => {
        setIsLoadingData(true);
        try {
            const {data} = await MicroQualificationService.getEducationProgramsGroup(1, 100, id);
            setEduProgramGroups(data.map(field => ({
                value: field.id,
                label: LocalName.getName(field) + " (" + field.code + ")",
                directionId: field.trainingDirection.id,
            })));
        } catch (error) {
            message.error(<IntlMessage id={'error'}/>);
        } finally {
            setIsLoadingData(false);
        }
    }, []);

    const getPartnerOrganizations = useCallback(async () => {
        if (partnerOrganizations.length === 0) {
            setIsLoadingData(true);
            try {
                const {data} = await MicroQualificationService.getPartnerOrganizations(1, 100);
                setPartnerOrganizations(data.map(field => ({
                    value: field.id,
                    label: LocalName.getName(field)
                })));
            } catch (error) {
                message.error(<IntlMessage id={'error'}/>);
            } finally {
                setIsLoadingData(false);
            }
        }
    }, [partnerOrganizations.length]);

    const handleEducationFieldSelect = useCallback((fieldId) => {
        form.setFieldValue("trainingDirection", null);
        form.setFieldValue("groupEP", null);
        setFilteredTrainDirections(trainDirections.filter(d => d.fieldId === fieldId));
        setFilteredEduProgramGroups([]);
    }, [form, trainDirections]);

    const handleTrainingDirectionSelect = useCallback((directionId) => {
        form.setFieldValue("groupEP", null);
        setFilteredEduProgramGroups(eduProgramGroups.filter(p => p.directionId === directionId));
    }, [form, eduProgramGroups]);

    const loadAllData = useCallback(async () => {
        await getEducationFields();
        await getTrainingDirections();
        await getEducationProgramsGroup();
        await getPartnerOrganizations();
    }, [getEducationFields, getTrainingDirections, getEducationProgramsGroup, getPartnerOrganizations]);

    // Автоматическая фильтрация при загрузке данных с сервера
    useEffect(() => {
        if (data && trainDirections.length > 0 && eduProgramGroups.length > 0) {
            const educationFieldId = data.secondStep?.educationFieldId;
            const trainingDirectionId = data.secondStep?.trainingDirectionId;

            if (educationFieldId) {
                const filtered = trainDirections.filter(d => d.fieldId === educationFieldId);
                setFilteredTrainDirections(filtered);
            }

            if (trainingDirectionId) {
                const filtered = eduProgramGroups.filter(p => p.directionId === trainingDirectionId);
                setFilteredEduProgramGroups(filtered);
            }
        }
    }, [data, trainDirections, eduProgramGroups]);

    return {
        educationFields,
        filteredTrainDirections,
        filteredEduProgramGroups,
        partnerOrganizations,
        isLoadingData,
        handleEducationFieldSelect,
        handleTrainingDirectionSelect,
        loadAllData
    };
};
